// Doctor Jobs Profile Context - Adapted from your other product
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../supabase";
import { useAuth } from "./AuthContext";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { subscribeToResendAudience } from "../hooks/useResendAudience";

const ProfileInfoContext = createContext(undefined);

export const useProfileInfo = () => {
  const context = useContext(ProfileInfoContext);
  if (!context) {
    throw new Error("useProfileInfo must be used within a ProfileInfoProvider");
  }
  return context;
};

export const ProfileInfoProvider = ({ children }) => {
  const { user } = useAuth();
  const { showError } = useSnackbarNotification();
  const [profileInfo, setProfileInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizeProfile = (profile) => {
    if (!profile || typeof profile !== 'object') return profile;
    if (profile.companyname ?? profile.company_name) {
      return {
        ...profile,
        companyname: profile.companyname ?? profile.company_name ?? null,
      };
    }
    return {
      ...profile,
      companyname: null,
    };
  };

  const getProfileInfo = useCallback(async (forceRefresh = false) => {
    if (!user) {
      console.warn("User not available");
      return null;
    }

    const cacheKey = `profile_${user.id}`;
    
    // If force refresh, clear cache first
    if (forceRefresh) {
      try {
        sessionStorage.removeItem(cacheKey);
      } catch (e) {
        // Failed to clear cache
      }
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const normalized = normalizeProfile(cachedData);
          setProfileInfo(normalized);
          return normalized;
        } catch (e) {
          sessionStorage.removeItem(cacheKey);
        }
      }
    }

    // Fetch from database
    const { data, error } = await supabase
      .from("users_profiles")
      .select("*")
      .eq("profileid", user.id)
      .single();

    if (error || !data) {
      return null;
    }

    const normalized = normalizeProfile(data);

    // Cache for 5 minutes
    sessionStorage.setItem(cacheKey, JSON.stringify(normalized));
    setTimeout(() => sessionStorage.removeItem(cacheKey), 5 * 60 * 1000);

    setProfileInfo(normalized);
    return normalized;
  }, [user]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      getProfileInfo().finally(() => setLoading(false));
    } else {
      // Clear profile info when user logs out
      setProfileInfo(null);
      setLoading(false);
      // Clear cached profile data
      try {
        const cacheKey = `profile_${user?.id || 'all'}`;
        sessionStorage.removeItem(cacheKey);
        // Clear all profile cache entries
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('profile_')) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [user, getProfileInfo]);

  // Listen for storage events and custom events to detect when cache is cleared (e.g., by admin verification)
  useEffect(() => {
    if (!user) return;

    const cacheKey = `profile_${user.id}`;
    
    const handleStorageChange = (e) => {
      // If our profile cache was removed, refresh the profile
      if (e.key === cacheKey && e.newValue === null) {
        getProfileInfo(true);
      }
    };

    const handleCustomEvent = (e) => {
      // If the cache was cleared for this user, refresh
      if (e.detail?.userId === user.id) {
        getProfileInfo(true);
      }
    };

    // Listen for storage events (triggered when cache is cleared in another tab/window)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom events (triggered when cache is cleared in same tab)
    window.addEventListener('profileCacheCleared', handleCustomEvent);

    // Also check periodically if cache was cleared (fallback for same-tab cache clearing)
    const checkInterval = setInterval(() => {
      const cached = sessionStorage.getItem(cacheKey);
      // If we have profileInfo but cache was cleared, refresh
      if (profileInfo && !cached) {
        getProfileInfo(true);
      }
    }, 3000); // Check every 3 seconds (less aggressive)

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileCacheCleared', handleCustomEvent);
      clearInterval(checkInterval);
    };
  }, [user, profileInfo, getProfileInfo]);

  // Subscribe to realtime updates so verification changes propagate instantly
  useEffect(() => {
    if (!user) return;

    const cacheKey = `profile_${user.id}`;

    const channel = supabase
      .channel(`profile_updates_${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'users_profiles', filter: `profileid=eq.${user.id}` },
        (payload) => {
          if (!payload?.new) return;
          const updatedProfile = normalizeProfile(payload.new);
          
          // Persist latest profile in cache
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(updatedProfile));
          } catch (e) {
            console.warn('Failed to cache realtime profile update:', e);
          }

          setProfileInfo(updatedProfile);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const createProfileInfo = async (data) => {
    if (!user) throw new Error("User not authenticated");

    const isEmployer = (data.role ?? user?.user_metadata?.role) === 'Employer';

    const insertData = {
      // map camelCase -> lowercase column names expected by Postgres
      profileid: user.id,
      email: user.email,
      role: data.role,
      onboardingstep: data.onboardingStep ?? data.onboardingstep,
      onboardingcompleted: data.onboardingCompleted ?? data.onboardingcompleted,
      firsttimelogin: data.firstTimeLogin ?? data.firsttimelogin,
      
      // Basic user info (for candidate display)
      firstname: user.user_metadata?.first_name || data.firstName,
      lastname: user.user_metadata?.last_name || data.lastName,
      
      // Employee personal info
      cellnumber: user.user_metadata?.cell_number || data.cellNumber,
      dateofbirth: user.user_metadata?.date_of_birth || data.dateOfBirth,
      sex: user.user_metadata?.sex || data.sex,
      linkedinurl: user.user_metadata?.linkedin_url || data.linkedinUrl,
      jobsearchstatus: user.user_metadata?.job_search_status || data.jobSearchStatus,
      hpcsa_reg_no: user.user_metadata?.hpcsa_reg_no || data.hpcsaRegNo,
      location_city_country: user.user_metadata?.location_city_country || user.user_metadata?.country || data.locationCityCountry,
      profile_image_url: user.user_metadata?.profile_image_url || data.profileImageUrl,
      profile_about: user.user_metadata?.profile_about || data.aboutText,
      profile_visibility: user.user_metadata?.profile_visibility || data.profileVisibility,
      car_license: user.user_metadata?.profile_car_license || data.carLicense,
      
      // Employee professional info
      profession: user.user_metadata?.profession || data.profession,
      years_experience: user.user_metadata?.years_experience || data.yearsExperience,
      cv_url: user.user_metadata?.profile_cv_url || data.cvUrl,
      cv_filename: user.user_metadata?.profile_cv_filename || data.cvFilename,
      
      // Skills and experience (as JSON)
      skills: user.user_metadata?.profile_skills ? JSON.stringify(user.user_metadata.profile_skills) : null,
      languages: user.user_metadata?.profile_languages ? JSON.stringify(user.user_metadata.profile_languages) : null,
      work_experiences: user.user_metadata?.profile_experiences ? JSON.stringify(user.user_metadata.profile_experiences) : null,
      educations: user.user_metadata?.profile_educations ? JSON.stringify(user.user_metadata.profile_educations) : null,
      certifications: user.user_metadata?.profile_certifications ? JSON.stringify(user.user_metadata.profile_certifications) : null,
      
      // Role preferences (as JSON)
      selected_categories: user.user_metadata?.my_profile?.profile_role?.categories ? JSON.stringify(user.user_metadata.my_profile.profile_role.categories) : null,
      selected_roles: user.user_metadata?.my_profile?.profile_role?.roles ? JSON.stringify(user.user_metadata.my_profile.profile_role.roles) : null,
      employment_types: user.user_metadata?.my_profile?.profile_role?.employmentTypes ? JSON.stringify(user.user_metadata.my_profile.profile_role.employmentTypes) : null,
    };

    if (isEmployer) {
      // Get company info from metadata as fallback (for onboarding data)
      const companyInfo = user?.user_metadata?.employer_profile?.companyInfo;
      
      const employerFields = {
        companyname: data.companyName ?? data.companyname ?? companyInfo?.companyName,
        industry: data.industry ?? companyInfo?.industry,
        companyurl: data.companyUrl ?? data.companyurl ?? companyInfo?.companyUrl,
        workemail: data.workEmail ?? data.workemail ?? companyInfo?.workEmail,
        logofilename: data.logoFileName ?? data.logofilename ?? companyInfo?.logoFileName,
        jobtitle: data.jobTitle ?? data.jobtitle,
        specialty: data.specialty,
        joblocation: data.jobLocation ?? data.joblocation,
        employmenttype: data.employmentType ?? data.employmenttype,
        workpreference: data.workPreference ?? data.workpreference,
        qualifications: data.qualifications,
        requireslicense: data.requiresLicense ?? data.requireslicense,
        sponsorship: data.sponsorship,
        company_description: data.companyAbout ?? data.companyabout ?? data.company_description ?? companyInfo?.about,
      };
      
      // Filter out undefined/null/empty string values to avoid schema errors for missing columns
      const filteredEmployerFields = Object.fromEntries(
        Object.entries(employerFields).filter(([_, value]) => value !== undefined && value !== null && value !== "")
      );
      
      Object.assign(insertData, filteredEmployerFields);
    }

    // Filter out empty strings from all fields to prevent integer type errors
    const filteredInsertData = Object.fromEntries(
      Object.entries(insertData).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    );

    // Upsert to avoid duplicate key errors if profile already exists
    const { error } = await supabase
      .from("users_profiles")
      .upsert([filteredInsertData], { onConflict: "profileid" });

    if (error) throw new Error(error.message);
    await getProfileInfo();
      try {
        await subscribeToResendAudience({
          email: user.email,
          firstName: user.user_metadata.first_name,
          lastName: user.user_metadata.last_name,
          role: data.role,
        });

        await supabase.auth.updateUser({
          data: { isFirstTimeUser: false },
        });
      } catch (err) {
        console.error("Failed to subscribe user to Resend audience:", err);
      }
  };

  const updateProfileInfo = async (data) => {
    if (!user) throw new Error("User not authenticated");

    const isEmployer = (data.role ?? user?.user_metadata?.role) === 'Employer';

    const updateData = {
      role: data.role,
      onboardingstep: data.onboardingStep ?? data.onboardingstep,
      onboardingcompleted: data.onboardingCompleted ?? data.onboardingcompleted,
      firsttimelogin: data.firstTimeLogin ?? data.firsttimelogin,
      profile_visibility: data.profileVisibility ?? data.profile_visibility,
      updatedat: new Date().toISOString(),
    };

    if (isEmployer) {
      // Get company info from metadata as fallback (for onboarding data)
      const companyInfo = user?.user_metadata?.employer_profile?.companyInfo;
      
      const employerFields = {
        companyname: data.companyName ?? data.companyname ?? companyInfo?.companyName,
        industry: data.industry ?? companyInfo?.industry,
        companyurl: data.companyUrl ?? data.companyurl ?? companyInfo?.companyUrl,
        workemail: data.workEmail ?? data.workemail ?? companyInfo?.workEmail,
        logofilename: data.logoFileName ?? data.logofilename ?? companyInfo?.logoFileName,
        jobtitle: data.jobTitle ?? data.jobtitle,
        specialty: data.specialty,
        joblocation: data.jobLocation ?? data.joblocation,
        employmenttype: data.employmentType ?? data.employmenttype,
        workpreference: data.workPreference ?? data.workpreference,
        qualifications: data.qualifications,
        requireslicense: data.requiresLicense ?? data.requireslicense,
        sponsorship: data.sponsorship,
        company_description: data.companyAbout ?? data.companyabout ?? data.company_description ?? companyInfo?.about,
      };
      
      // Filter out undefined/null/empty string values to avoid schema errors for missing columns
      const filteredEmployerFields = Object.fromEntries(
        Object.entries(employerFields).filter(([_, value]) => value !== undefined && value !== null && value !== "")
      );
      
      Object.assign(updateData, filteredEmployerFields);
    }

    // Filter out empty strings from all fields to prevent integer type errors
    const filteredUpdateData = Object.fromEntries(
      Object.entries(updateData).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    );

    const { data: updated, error } = await supabase
      .from("users_profiles")
      .update(filteredUpdateData)
      .eq("profileid", user.id)
      .select("*")
      .single();

    if (error) throw new Error(error.message);
    
    if (updated) {
      // Update cache
      const cacheKey = `profile_${user.id}`;
      const normalized = normalizeProfile(updated);
      sessionStorage.setItem(cacheKey, JSON.stringify(normalized));
      setProfileInfo(normalized);
    }
  };

  const migrateFromMetadata = async () => {
    if (!user) return;

    try {
      const metadata = user.user_metadata;
      if (!metadata) return;

      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from("users_profiles")
        .select("profileid")
        .eq("profileid", user.id)
        .single();

      if (existingProfile) {
        return;
      }

      // Migrate data from metadata
      const profileData = {
        role: 'Employee', // Default to employee for existing users
        fullName: metadata.first_name && metadata.last_name 
          ? `${metadata.first_name} ${metadata.last_name}` 
          : null,
        employeeLocation: metadata.location_city_country || metadata.country,
        linkedinUrl: metadata.linkedin_url,
        jobSearchStatus: metadata.job_search_status,
        hpcsaRegNo: metadata.hpcsa_reg_no,
        dateOfBirth: metadata.date_of_birth,
        sex: metadata.sex,
        cvUrl: metadata.profile_cv_url,
        cvPath: metadata.profile_cv_path,
        cvFilename: metadata.profile_cv_filename,
        aboutText: metadata.profile_about,
        profileVisibility: metadata.profile_visibility || 'hidden',
        carLicense: metadata.profile_car_license,
        skills: metadata.profile_skills?.skills || [],
        languages: metadata.profile_skills?.languages || [],
        workExperiences: metadata.profile_experience?.experiences || [],
        educations: metadata.profile_experience?.educations || [],
        certifications: metadata.profile_experience?.certifications || [],
        onboardingCompleted: true, // Mark as completed since they have data
        firstTimeLogin: false
      };

      await createProfileInfo(profileData);
      
    } catch (error) {
      showError("Failed to migrate profile data");
    }
  };

  return (
    <ProfileInfoContext.Provider
      value={{ 
        profileInfo, 
        loading, 
        createProfileInfo, 
        getProfileInfo, 
        updateProfileInfo,
        migrateFromMetadata
      }}
    >
      {children}
    </ProfileInfoContext.Provider>
  );
};
